import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fetchMesaForQR } from '../../../api/qrApi'
import { PaymentSection } from './PaymentSection'

interface TableViewProps {
  mesaId: string
}

/**
 * QR View - Displays mesa data for customers
 * 
 * ✅ QR ONLY calls ONE endpoint: /qrApi/mesa/:mesaId
 * ❌ QR NEVER talks to Firestore or POS directly
 * ❌ QR NEVER resolves tickets or computes payments
 * 
 * All data comes from the mesa document (synced by admin)
 * 
 * QR UI RULES:
 * ❌ No Firestore reads
 * ❌ No POS calls
 * ❌ No listeners
 * ❌ No derived totals
 * ✅ Render-only
 * ✅ One API
 * ✅ One state object
 */
export function TableView({ mesaId }: TableViewProps) {
  const [searchParams] = useSearchParams()
  const ticket = searchParams.get('ticket') ?? undefined
  const [mesa, setMesa] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function loadMesa() {
      try {
        if (!mesaId) {
          throw new Error('mesaId is required')
        }

        const data = await fetchMesaForQR(mesaId, ticket)
        setMesa(data)
      } catch (e: any) {
        console.error('Failed to load mesa:', e)
        setError(e.message || 'No se pudo cargar la cuenta')
      } finally {
        setLoading(false)
      }
    }

    loadMesa()
  }, [mesaId, ticket])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Cargando datos del ticket…</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center max-w-md">
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      </div>
    )
  }

  if (!mesa) return null

  // PaymentSection now handles the full UI including header
  return <PaymentSection mesa={mesa} />
}
